import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

interface SegmentedControlProps {
  tabs: string[]
  selectedTab: string
  onTabChange: (tab: string) => void
  style?: React.CSSProperties
}

const ControlWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 3px;
  border: 1px solid var(--border, #e3e7ef);
  border-radius: 11px;
  background: var(--surface-hover, #f7f9fc);
  position: relative;
  z-index: 1;
`

const SegmentButton = styled.button<{ $active?: boolean }>`
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: ${props => props.$active ? 'var(--ink-strong, #10141f)' : 'var(--text-muted, #6f7686)'};
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1;
  padding: 8px 16px;
  transition: color 0.16s ease;
  user-select: none;
  position: relative;
  z-index: 1;

  &:hover {
    color: var(--ink-strong, #10141f);
  }
`

const SlidingHighlight = styled.div<{ $left: number, $width: number }>`
  position: absolute;
  top: 3px;
  bottom: 3px;
  left: 0;
  width: ${({ $width }) => $width}px;
  transform: translateX(${({ $left }) => $left}px);
  background: var(--surface, #ffffff);
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);
  transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1), width 0.22s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 0;
`

export const SegmentedControl = ({
  tabs,
  selectedTab,
  onTabChange,
  style
}: SegmentedControlProps): React.ReactElement => {
  const tabRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({})
  const [highlightStyle, setHighlightStyle] = useState({ left: 0, width: 0 })

  useEffect(() => {
    const activeEl = tabRefs.current[selectedTab]
    if (activeEl) {
      setHighlightStyle({
        left: activeEl.offsetLeft,
        width: activeEl.offsetWidth
      })
    }
  }, [selectedTab, tabs])

  return (
    <ControlWrapper style={style}>
      <SlidingHighlight $left={highlightStyle.left} $width={highlightStyle.width} />
      {tabs.map(tab => (
        <SegmentButton
          key={tab}
          ref={el => (tabRefs.current[tab] = el)}
          type="button"
          $active={tab === selectedTab}
          onClick={() => onTabChange(tab)}
        >
          {tab}
        </SegmentButton>
      ))}
    </ControlWrapper>
  )
}

export default SegmentedControl
